import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Modal, Form, Input, Select, Spin, Alert, message } from 'antd';
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { useCoins, useCreateCoin, useUpdateCoin, useDeleteCoin } from '../hooks/useCoinManagement';
import { getUserIdFromToken } from '../utils/authUtils';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../utils/notificationUtils';
import { showConfirmModal } from '../utils/modalUtils';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;
const { Option } = Select;

const CoinsManagementPage: React.FC = () => {
  const { t } = useTranslationWithNamespace('coins');
  const { t: statusT } = useTranslationWithNamespace('status');
  const { t: commonT } = useTranslationWithNamespace('common');
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState<any>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filteredCoins, setFilteredCoins] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch coins data
  const {
    data: coinsData,
    isLoading: isLoadingCoins,
    isError: isCoinsError,
    refetch: refetchCoins
  } = useCoins();

  // Mutations for CRUD operations
  const createCoinMutation = useCreateCoin();
  const updateCoinMutation = useUpdateCoin();
  const deleteCoinMutation = useDeleteCoin();

  // Format coins data for table
  const formattedCoins = useMemo(() => {
    // Ensure coinsData is an array before mapping
    if (!coinsData || !Array.isArray(coinsData)) {
      console.warn('coinsData is not an array:', coinsData);
      return [];
    }

    return coinsData.map((coin: any) => ({
      key: coin._id || coin.id,
      id: coin._id || coin.id,
      symbol: coin.symbol,
      full_name: coin.full_name,
      type: coin.type,
      status: coin.status,
      logo: coin.logo || 'https://via.placeholder.com/30', // Provide a fallback for logo
      description: coin.description
    }));
  }, [coinsData]);

  // Filter coins based on search text
  useEffect(() => {
    if (!searchText) {
      setFilteredCoins(formattedCoins);
      setCurrentPage(1); // Reset to first page when clearing search
      return;
    }

    const filtered = formattedCoins.filter((coin: any) =>
      (coin.symbol || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (coin.full_name || '').toLowerCase().includes(searchText.toLowerCase())
    );

    setFilteredCoins(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [formattedCoins, searchText]);

  // Table columns
  const columns = [
    {
      title: t('logo'),
      dataIndex: 'logo',
      key: 'logo',
      render: (logo: string) => (
        <img
          src={logo || 'https://via.placeholder.com/30'}
          alt="Coin Logo"
          style={{ width: 30, height: 30, objectFit: 'contain' }}
          onError={(e) => {
            // Replace broken image with placeholder
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/30';
          }}
        />
      )
    },
    {
      title: t('symbol'),
      dataIndex: 'symbol',
      key: 'symbol',
    },
    {
      title: t('fullName'),
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const color = type === 'Coin' ? 'blue' : 'purple';
        return <Tag color={color}>{type}</Tag>;
      }
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'Active' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: commonT('actions.title'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {commonT('actions.edit')}
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {commonT('actions.delete')}
          </Button>
        </Space>
      )
    }
  ];

  // Handle adding a new coin
  const handleAdd = () => {
    setIsAddModalVisible(true);
    form.resetFields();
  };

  // Handle editing a coin
  const handleEdit = (coin: any) => {
    setSelectedCoin(coin);
    setIsEditModalVisible(true);
    form.setFieldsValue({
      symbol: coin.symbol,
      full_name: coin.full_name,
      type: coin.type,
      status: coin.status,
      logo: coin.logo,
      description: coin.description
    });
  };

  // Handle deleting a coin
  const handleDelete = (coin: any) => {
    // Use our showConfirmModal utility to avoid the "Static function can not consume context like dynamic theme" warning
    showConfirmModal({
      title: t('coin.confirmDelete'),
      content: t('coin.deleteWarning', { symbol: coin.symbol }),
      okText: t('actions.delete'),
      okType: 'danger',
      cancelText: t('actions.cancel'),
      onOk: async () => {
        try {
          const response = await deleteCoinMutation.mutateAsync(coin.id);

          // Check if the response indicates a soft delete (marked as inactive)
          if (response && response.message && response.message.includes('marked as inactive')) {
            // This is a soft delete response
            const dependencies = response.dependencies || {};
            const networkCount = dependencies.networks || 0;
            const coinNetworkCount = dependencies.coinNetworks || 0;
            const walletCount = dependencies.wallets || 0;

            let message = t('coin.markedInactiveMessage', 'The coin was marked as inactive because it is referenced by other records:');

            if (networkCount > 0) {
              message += `\n- ${t('coin.usedAsBaseCoin', { count: networkCount, defaultValue: 'Used as base coin in {{count}} network(s)' })}`;
            }
            if (coinNetworkCount > 0) {
              message += `\n- ${t('coin.usedInCoinNetworks', { count: coinNetworkCount, defaultValue: 'Used in {{count}} coin-network relationship(s)' })}`;
            }
            if (walletCount > 0) {
              message += `\n- ${t('coin.usedByWallets', { count: walletCount, defaultValue: 'Used by {{count}} wallet(s)' })}`;
            }

            message += `\n\n${t('coin.removeDepMessage', 'To completely remove this coin, you must first remove these dependencies.')}`;

            showWarningNotification(
              t('coin.markedInactive', 'Coin marked as inactive'),
              message
            );
          } else {
            // Regular successful deletion
            showSuccessNotification(t('coin.deleteSuccess', 'Coin deleted successfully'));
          }

          // Reset search input after successful operation
          if (searchText) {
            setSearchText('');
          }
        } catch (error: any) {
          console.error('Error deleting coin:', error);

          // Check for dependency errors in error response
          if (error.response && error.response.data && error.response.data.error) {
            const errorMessage = error.response.data.error.message || '';

            if (errorMessage.includes('Cannot delete coin') ||
              errorMessage.includes('used as a base coin') ||
              errorMessage.includes('coin-network relationship')) {
              // Show specific error for dependencies
              showWarningNotification(
                t('coin.dependencyError', 'This coin cannot be deleted because it is used in other parts of the system.'),
                t('coin.markInactiveInstead', 'Consider marking it as inactive instead.')
              );

              // Ask if user wants to mark as inactive instead
              showConfirmModal({
                title: t('coin.markInactiveTitle', 'Mark as Inactive?'),
                content: t('coin.markInactiveContent', 'Would you like to mark this coin as inactive instead of deleting it?'),
                okText: t('actions.yes'),
                cancelText: t('actions.no'),
                onOk: async () => {
                  try {
                    // Update coin status to inactive
                    await updateCoinMutation.mutateAsync({
                      id: coin.id,
                      coinData: {
                        ...coin,
                        status: 'Inactive',
                        creator_id: 123 // Using a hardcoded numeric value as required by the model
                      }
                    });
                    showSuccessNotification(t('coin.markedInactive', 'Coin marked as inactive successfully'));

                    // Reset search input after marking as inactive
                    if (searchText) {
                      setSearchText('');
                    }
                  } catch (updateError) {
                    console.error('Error updating coin status:', updateError);
                    showErrorNotification(t('coin.updateError', 'Failed to update coin status'));
                  }
                }
              });
              return;
            }
          }

          // Generic error message for other errors
          showErrorNotification(t('coin.deleteError', 'Failed to delete coin'));
        }
      }
    });
  };

  // Handle form submission for adding a coin
  const handleAddFormSubmit = async (values: any) => {
    try {
      // Get user ID from token
      const userId = getUserIdFromToken();

      if (!userId) {
        message.error(t('coin.noUserId', 'User ID not found. Please log in again.'));
        return;
      }

      // Add creator_id to the coin data - use a hardcoded numeric value as required by the model
      // The model expects a Number, not a string ObjectId
      const coinData = {
        ...values,
        creator_id: 123 // Using a hardcoded numeric value as required by the model
      };

      await createCoinMutation.mutateAsync(coinData);
      showSuccessNotification(t('coin.addSuccess', 'Coin added successfully'));
      setIsAddModalVisible(false);

      // Reset search input after adding a coin
      if (searchText) {
        setSearchText('');
      }
    } catch (error: any) {
      console.error('Error adding coin:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        showErrorNotification(
          t('coin.addError', 'Failed to add coin'),
          errorMessage
        );
      } else {
        showErrorNotification(t('coin.addError', 'Failed to add coin'));
      }
    }
  };

  // Handle form submission for editing a coin
  const handleEditFormSubmit = async (values: any) => {
    try {
      // Get user ID from token
      const userId = getUserIdFromToken();

      if (!userId) {
        message.error(t('coin.noUserId', 'User ID not found. Please log in again.'));
        return;
      }

      // Add creator_id to the coin data - use a hardcoded numeric value as required by the model
      const coinData = {
        ...values,
        creator_id: 123 // Using a hardcoded numeric value as required by the model
      };

      await updateCoinMutation.mutateAsync({
        id: selectedCoin.id,
        coinData: coinData
      });
      showSuccessNotification(t('coin.updateSuccess', 'Coin updated successfully'));
      setIsEditModalVisible(false);

      // Reset search input after updating a coin
      if (searchText) {
        setSearchText('');
      }
    } catch (error: any) {
      console.error('Error updating coin:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        showErrorNotification(
          t('coin.updateError', 'Failed to update coin'),
          errorMessage
        );
      } else {
        showErrorNotification(t('coin.updateError', 'Failed to update coin'));
      }
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{t('title', 'Coins Management')}</Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder={t('searchPlaceholder', 'Search by Symbol or Name...')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchCoins()}
              loading={isLoadingCoins}
            >
              {commonT('actions.refresh', 'Refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('addCoin', 'Add Coin')}
            </Button>
          </Space>
        </Col>
      </Row>

      {isCoinsError && (
        <Alert
          message={t('coin.errorTitle', 'Error Loading Coins')}
          description={t('coin.errorDescription', 'There was an error loading the coins data. Please try refreshing the page.')}
          type="error"
          showIcon
          style={{ marginBottom: 16, marginTop: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        {isLoadingCoins ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredCoins}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredCoins.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1); // Reset to first page when page size changes
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1); // Reset to first page when page size changes
              }
            }}
            rowKey="key"
            locale={{
              emptyText: searchText
                ? t('noCoinsFound', 'No coins match your search')
                : t('noCoins', 'No coins found')
            }}
          />
        )}
      </Card>

      {/* Modal for adding a new coin */}
      <Modal
        title={t('addCoin', 'Add Coin')}
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        footer={null}
        confirmLoading={createCoinMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddFormSubmit}
          disabled={createCoinMutation.isPending}
        >
          <Form.Item
            name="symbol"
            label={t('symbol')}
            rules={[{ required: true, message: t('symbolRequired') }]}
          >
            <Input placeholder={t('symbolPlaceholder', 'e.g. BTC')} />
          </Form.Item>
          <Form.Item
            name="full_name"
            label={t('fullName')}
            rules={[{ required: true, message: t('fullNameRequired') }]}
          >
            <Input placeholder={t('fullNamePlaceholder', 'e.g. Bitcoin')} />
          </Form.Item>
          <Form.Item
            name="type"
            label={t('type')}
            rules={[{ required: true, message: t('typeRequired') }]}
          >
            <SearchableSelect
              placeholder={t('selectType')}
              searchPlaceholder="Type to search coin types..."
            >
              <Option value="Coin">{t('typeCoin', 'Coin')}</Option>
              <Option value="Token">{t('typeToken', 'Token')}</Option>
              <Option value="Fiat">{t('typeFiat', 'Fiat')}</Option>
              <Option value="NFT">{t('typeNFT', 'NFT')}</Option>
              <Option value="SPL">{t('typeSPL', 'SPL')}</Option>
            </SearchableSelect>
          </Form.Item>
          <Form.Item
            name="status"
            label={t('status')}
            rules={[{ required: true, message: t('statusRequired') }]}
          >
            <SearchableSelect
              placeholder={t('selectStatus')}
              searchPlaceholder="Type to search status..."
            >
              <Option value="Active">{statusT('active', 'Active')}</Option>
              <Option value="Inactive">{statusT('inactive', 'Inactive')}</Option>
            </SearchableSelect>
          </Form.Item>
          <Form.Item
            name="logo"
            label={t('logo')}
          >
            <Input placeholder={t('logoPlaceholder', 'Logo URL')} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('description')}
          >
            <Input.TextArea
              placeholder={t('descriptionPlaceholder', 'Enter coin description')}
              rows={3}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {commonT('actions.submit', 'Submit')}
            </Button>
            <Button onClick={() => setIsAddModalVisible(false)}>
              {commonT('actions.cancel', 'Cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for editing a coin */}
      <Modal
        title={t('editCoin', 'Edit Coin')}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        confirmLoading={updateCoinMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditFormSubmit}
          disabled={updateCoinMutation.isPending}
        >
          <Form.Item
            name="symbol"
            label={t('symbol')}
            rules={[{ required: true, message: t('symbolRequired') }]}
          >
            <Input placeholder={t('symbolPlaceholder', 'e.g. BTC')} />
          </Form.Item>
          <Form.Item
            name="full_name"
            label={t('fullName')}
            rules={[{ required: true, message: t('fullNameRequired') }]}
          >
            <Input placeholder={t('fullNamePlaceholder', 'e.g. Bitcoin')} />
          </Form.Item>
          <Form.Item
            name="type"
            label={t('type')}
            rules={[{ required: true, message: t('typeRequired') }]}
          >
            <SearchableSelect
              placeholder={t('selectType')}
              searchPlaceholder="Type to search coin types..."
            >
              <Option value="Coin">{t('typeCoin', 'Coin')}</Option>
              <Option value="Token">{t('typeToken', 'Token')}</Option>
              <Option value="Fiat">{t('typeFiat', 'Fiat')}</Option>
              <Option value="NFT">{t('typeNFT', 'NFT')}</Option>
              <Option value="SPL">{t('typeSPL', 'SPL')}</Option>
            </SearchableSelect>
          </Form.Item>
          <Form.Item
            name="status"
            label={t('status')}
            rules={[{ required: true, message: t('statusRequired') }]}
          >
            <SearchableSelect
              placeholder={t('selectStatus')}
              searchPlaceholder="Type to search status..."
            >
              <Option value="Active">{statusT('active', 'Active')}</Option>
              <Option value="Inactive">{statusT('inactive', 'Inactive')}</Option>
            </SearchableSelect>
          </Form.Item>
          <Form.Item
            name="logo"
            label={t('logo')}
          >
            <Input placeholder={t('logoPlaceholder', 'Logo URL')} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('description')}
          >
            <Input.TextArea
              placeholder={t('descriptionPlaceholder', 'Enter coin description')}
              rows={3}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {commonT('actions.submit', 'Submit')}
            </Button>
            <Button onClick={() => setIsEditModalVisible(false)}>
              {commonT('actions.cancel', 'Cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CoinsManagementPage;
